class OptionWidthPrimeRange extends HTMLElement {
    // Cache for fetched product HTML by URL
    static productHtmlCache = {};
  constructor() {
    super();
    this.selectors = {
      slider: '[data-width-prime-slider]',
      selectedValue: '[data-selected-value]',
      exactInput: '[data-exact-dimension-input]',
      container: '.length-range',
      knob: '[data-knob-value]',
      json: 'script[type="application/json"]',
      variantsBlock: '.product__block__variants'
    };
    this.is_first = true;
    this._updatingRipCutPrice = false;
    this._boundRipCutChange = this.handleRipCutDependencyChange.bind(this);
    this._boundSliderChange = this.handleSliderChange.bind(this);
  }

  isPriceDebugEnabled() {
    if (window.WIDTH_PRIME_PRICE_DEBUG === true) return true;
    if (this.hasAttribute('data-price-debug')) return true;
    try {
      return new URLSearchParams(window.location.search).has('price_debug');
    } catch {
      return false;
    }
  }

  formatCents(cents) {
    const currencyFormatter = window.themeVendor?.themeCurrency;
    if (currencyFormatter && window.theme) {
      return currencyFormatter.formatMoney(cents, theme.moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  getSelectedOptionValues() {
    const variantBlock = document.querySelector(this.selectors.variantsBlock);
    if (!variantBlock) return [];

    const optionElements = Array.from(variantBlock.querySelectorAll('[data-option-position]'));
    optionElements.sort((a, b) =>
      parseInt(a.getAttribute('data-option-position'), 10) - parseInt(b.getAttribute('data-option-position'), 10)
    );

    return optionElements.map(optionEl => {
      const hiddenInput = optionEl.querySelector('input[type="hidden"]');
      if (hiddenInput?.value) return hiddenInput.value;

      const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
      if (checkedRadio) return checkedRadio.value;

      const checkedSwatch = optionEl.querySelector('[swatch-option-selector]:checked, [data-addon-input]:checked');
      if (checkedSwatch) return checkedSwatch.value;

      const activeLink = optionEl.querySelector('a.active[data-value], a[data-value].active');
      if (activeLink) return activeLink.getAttribute('data-value');

      return null;
    });
  }

  getVariantBreakdown() {
    const priceEl = document.querySelector('[data-product-price]');
    const variantInput = document.querySelector("input[type='hidden'][name='id']");
    const rawBase = priceEl?.getAttribute('data-base-price');
    const cents = rawBase !== null ? (parseInt(rawBase, 10) || 0) : null;
    const options = this.getSelectedOptionValues().filter(Boolean);
    const variantId = variantInput?.value || window.currentMatchingVariant?.id || null;

    let detail = variantId ? `variant #${variantId}` : 'variant (id pending)';
    if (options.length) detail += ` — ${options.join(' / ')}`;

    const widthTier = window.currentWidthProduct?.product_metafields?.width;
    if (widthTier) detail += ` @ ${widthTier}mm width tier`;

    return { cents, detail, label: 'VARIANT' };
  }

  getBracketAddonBreakdown() {
    const el = document.querySelector('addon-bracket-selector');
    if (!el) return { cents: 0, detail: 'not on this product', label: 'ADDON' };

    const state = window.bracketAddonState;
    if (!state) return { cents: 0, detail: 'bracket add-on (loading)', label: 'ADDON' };
    if (!state.enabled) return { cents: 0, detail: 'bracket add-on off', label: 'ADDON' };
    if (!state.totalCents) return { cents: 0, detail: 'bracket add-on on — £0', label: 'ADDON' };

    const title = state.title || 'bracket';
    const detail = `${state.quantity} × ${this.formatCents(state.unitCents)} — ${title}${state.variantId ? ` (#${state.variantId})` : ''}`;
    return { cents: state.totalCents, detail, label: 'ADDON' };
  }

  getRipCutBreakdown() {
    const rip = window.ripCutState || {};
    const widthMm = parseInt(this.slider?.value, 10);

    if (!rip.active) {
      const reason = !rip.id
        ? 'no rip-cut product'
        : `width ${widthMm}mm ≥ 225mm`;
      return { cents: 0, detail: `off (${reason})`, label: 'RIP CUT' };
    }

    const detail = `${rip.quantity} ft × ${this.formatCents(rip.unitPriceCents)}${rip.id ? ` (variant #${rip.id})` : ''}`;
    return { cents: rip.totalPriceCents || 0, detail, label: 'RIP CUT' };
  }

  getPriceBreakdown() {
    const variant = this.getVariantBreakdown();
    const addon = this.getBracketAddonBreakdown();
    const ripCut = this.getRipCutBreakdown();
    const priceEl = document.querySelector('[data-product-price]');
    const displayedCents = priceEl ? this.parseDisplayedMoney(priceEl.textContent) : NaN;

    const variantCents = variant.cents ?? 0;
    const addonCents = addon.cents || 0;
    const ripCents = ripCut.cents || 0;
    const sumCents = variantCents + addonCents + ripCents;

    return {
      variant,
      addon,
      ripCut,
      total: {
        cents: Number.isFinite(displayedCents) ? displayedCents : sumCents,
        sumCents,
        detail: Number.isFinite(displayedCents) && displayedCents !== sumCents
          ? `display differs from sum by ${this.formatCents(Math.abs(displayedCents - sumCents))} (theme still updating?)`
          : 'variant + addon + rip cut'
      }
    };
  }

  schedulePriceSummaryLog(trigger, delay = 150) {
    if (!this.isPriceDebugEnabled()) return;
    clearTimeout(this._priceSummaryTimer);
    this._priceSummaryTimer = setTimeout(() => this.logPriceSummary(trigger), delay);
  }

  logPriceSummary(trigger) {
    if (!this.isPriceDebugEnabled()) return;

    const widthMm = parseInt(this.slider?.value, 10);
    const lengthSlider = document.querySelector('[data-option-range-slider]');
    const lengthCm = parseFloat(lengthSlider?.value);
    const { variant, addon, ripCut, total } = this.getPriceBreakdown();

    const row = (item) => {
      const amount = item.cents !== null && item.cents !== undefined
        ? this.formatCents(item.cents)
        : '—';
      return { LAYER: item.label, AMOUNT: amount, DETAIL: item.detail };
    };

    console.log(
      `%c[PRICE CHANGE] ${trigger}`,
      'font-size:13px;font-weight:700;color:#1d4ed8;background:#eff6ff;padding:2px 6px;border-radius:3px',
      `(width ${widthMm}mm · length ${Number.isFinite(lengthCm) ? lengthCm + 'cm' : '?'})`
    );
    console.table([
      row(variant),
      row(addon),
      row(ripCut),
      { LAYER: 'TOTAL', AMOUNT: this.formatCents(total.cents), DETAIL: total.detail }
    ]);
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.slider = this.querySelector(this.selectors.slider);
    this.selectedValueDisplay = this.querySelector(this.selectors.selectedValue);
    this.exactInput = this.querySelector(this.selectors.exactInput);
    this.container = this.querySelector(this.selectors.container);
    this.knob = this.querySelector(this.selectors.knob);
    this.json = this.querySelector(this.selectors.json);
    if (this.json) { this.jsonData = JSON.parse(this.json.textContent); }

    if (!this.container || !this.slider) return;

    this.setupSlider();
    if (this.exactInput) {
      this.exactInput.removeAttribute('min');
      this.exactInput.removeAttribute('max');
      this.exactInput.addEventListener('input', this.handleExactInputChange.bind(this));
      this.exactInput.addEventListener('blur', this.handleExactInputBlur.bind(this));
    }
    this.findOrCreateLineItemInput();
    this.setupEventListeners();
    document.addEventListener('length-changed', this._boundRipCutChange);
    document.addEventListener('theme:variant:change', this._boundRipCutChange);
    document.addEventListener('variants-block:updated', this._boundRipCutChange);
    this._boundBracketChange = () => this.schedulePriceSummaryLog('addon change', 120);
    document.addEventListener('bracket-addon:change', this._boundBracketChange);
    this.observeProductUpdates();
    this.updateInitialDisplay();
    this.schedulePriceSummaryLog('page load', 900);
  }

  disconnectedCallback() {
    document.removeEventListener('length-changed', this._boundRipCutChange);
    document.removeEventListener('theme:variant:change', this._boundRipCutChange);
    document.removeEventListener('variants-block:updated', this._boundRipCutChange);
    document.removeEventListener('bracket-addon:change', this._boundBracketChange);
    if (this._productObserver) this._productObserver.disconnect();
    if (this._ripCutPriceTimer) clearTimeout(this._ripCutPriceTimer);
    if (this._widthVariantSyncTimer) clearTimeout(this._widthVariantSyncTimer);
    if (this._variantDebounceTimer) clearTimeout(this._variantDebounceTimer);
    if (this._priceSummaryTimer) clearTimeout(this._priceSummaryTimer);
  }

  setupSlider() {
    const min = parseFloat(this.slider.getAttribute('min')) || 5;
    const max = parseFloat(this.slider.getAttribute('max')) || 250;
    const step = parseFloat(this.slider.getAttribute('step')) || 1;
    this.slider.min = min;
    this.slider.max = max;
    this.slider.step = step;

    const value = parseFloat(this.slider.value);
    if (!Number.isFinite(value) || value < min) {
      this.slider.value = min;
    } else if (value > max) {
      this.slider.value = max;
    }
  }

  findOrCreateLineItemInput() {
    const productForm = this.closest('.product-section')?.querySelector('form[action^="/cart/add"][data-product-form]');

    if (!productForm) return;

    this.lineItemInput = productForm.querySelector('input[data-width-property]');

    if (!this.lineItemInput) { this.lineItemInput = productForm.querySelector('input[name="properties[Customer Selected Width]"]'); }

    if (!this.lineItemInput) {
      this.lineItemInput = document.createElement('input');
      this.lineItemInput.type = 'hidden';
      this.lineItemInput.name = 'properties[Customer Selected Width]';
      this.lineItemInput.setAttribute('data-width-property', '');
      productForm.appendChild(this.lineItemInput);
    }
  }

  updateLineItemProperty(value) {
    if (!this.lineItemInput) return;

    const numericValue = parseFloat(value);
    const inches = (numericValue / 25.4).toFixed(1);
    this.lineItemInput.value = `${numericValue}mm (approx ${inches}in)`;
  }

  setupEventListeners() {
    this.slider.removeEventListener('input', this._boundSliderChange);
    this.slider.addEventListener('input', this._boundSliderChange);
    this.initializeRipCutFromSlider();
  }

  initializeRipCutFromSlider() {
    const widthValue = parseInt(this.slider.value, 10);

    if (this.jsonData?.length > 0) {
      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        window.currentWidthProduct = nearestProduct;
        this.applyRipCutMetadata(nearestProduct);
      }
    }

    this.updateRipCutState(widthValue, false);

    // Let the theme, length slider, and any bracket add-ons settle first.
    // Only touch the visible price when rip cut actually applies.
    if (window.ripCutState?.active) {
      this.scheduleRipCutPriceUpdate(500);
    }

    this.dispatchEvent(new CustomEvent('width-prime-changed', {
      detail: { value: widthValue },
      bubbles: true
    }));
    this.is_first = false;
  }

  updateInitialDisplay() {
    const currentValue = parseInt(this.slider.value);

    requestAnimationFrame(() => {
      this.updateKnob(currentValue);
      this.updateSelectedValueDisplay(currentValue);
      this.updateLineItemProperty(currentValue);
    });
  }

  handleExactInputChange(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) return;

    // Only update slider if within bounds, otherwise let user keep typing
    if (val >= min && val <= max) {
      this.slider.value = val;
      this.handleSliderChange({ target: this.slider });
    }
  }

  handleExactInputBlur(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) {
      val = min;
    } else if (val < min) {
      val = min;
    } else if (val > max) {
      val = max;
    }

    event.target.value = val;
    this.slider.value = val;
    this.handleSliderChange({ target: this.slider });
  }

  handleSliderChange(event) {
    const widthValue = parseInt(event.target.value);

    if (this.exactInput && parseInt(this.exactInput.value) !== widthValue) {
      this.exactInput.value = widthValue;
    }

    this.updateKnob(widthValue);
    this.updateLineItemProperty(widthValue);
    this.updateSelectedValueDisplay(widthValue);

    // OPTIONAL: Use JSON data if available (for variant matching, title updates, etc.)
    if (this.jsonData && this.jsonData.length > 0) {
      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        window.currentWidthProduct = nearestProduct;
        this.applyRipCutMetadata(nearestProduct);
        // Update product title if available
        if (nearestProduct.product_metafields?.title) {
          const titleEl = document.querySelector('.product__title');
          if (titleEl) {
            titleEl.textContent = nearestProduct.product_metafields.title;
          }
        }

        if (this.is_first === false) {
          this.debounceVariantChange(nearestProduct, 650);
        }
      }
    }

    // Width changes can also select another product/variant. Keep the rip-cut
    // state current now, but add its price only after that render has settled.
    this.updateRipCutState(widthValue, false);
    const priceEl = document.querySelector('[data-product-price]');
    const hadRipCutApplied = priceEl?.hasAttribute('data-rip-cut-applied-cents');
    if (window.ripCutState?.active || hadRipCutApplied) {
      this.scheduleRipCutPriceUpdate(700);
    }

    this.schedulePriceSummaryLog('width change', 800);

    this.dispatchEvent(new CustomEvent('width-prime-changed', {
      detail: { value: widthValue },
      bubbles: true
    }));
    this.is_first = false;
  }

  applyRipCutMetadata(product) {
    const cutId = product?.product_metafields?.cut;
    const cutPrice = product?.product_metafields?.cut_price;

    if (cutId) {
      this.slider.setAttribute('data-variant-cut', cutId);
      this.slider.setAttribute('data-variant-cut-price', Number(cutPrice) || 0);
    } else {
      this.slider.removeAttribute('data-variant-cut');
      this.slider.removeAttribute('data-variant-cut-price');
    }
  }

  getRipCutQuantity() {
    const lengthSlider = document.querySelector('[data-option-range-slider]');
    const lengthCm = parseFloat(lengthSlider?.value);
    if (!Number.isFinite(lengthCm) || lengthCm <= 0) return 1;

    // Rip cut is sold per foot. Convert the selected centimetres to feet and
    // always round upward (for example, 5.5ft becomes 6 units).
    return Math.max(1, Math.ceil(lengthCm / 30.48));
  }

  updateRipCutState(widthValue = parseFloat(this.slider?.value), applyPrice = true) {
    const hasRipCut = this.slider?.hasAttribute('data-variant-cut');
    const cutId = hasRipCut ? this.slider.getAttribute('data-variant-cut') : null;
    const active = Boolean(cutId) && Number(widthValue) < 225;
    const quantity = active ? this.getRipCutQuantity() : 0;
    const unitPriceCents = active
      ? (parseInt(this.slider.getAttribute('data-variant-cut-price'), 10) || 0)
      : 0;

    window.addonCutActive = active;
    window.addonCutId = active ? cutId : null;
    window.ripCutState = {
      active,
      id: active ? cutId : null,
      quantity,
      unitPriceCents,
      totalPriceCents: unitPriceCents * quantity
    };

    if (!applyPrice) {
      this.schedulePriceSummaryLog('rip cut state', 100);
      return;
    }

    document.dispatchEvent(new CustomEvent('rip-cut:change', {
      detail: window.ripCutState
    }));
    this.updateProductPrice();
  }

  handleRipCutDependencyChange(event) {
    // Length change affects rip cut quantity (per foot) and variant options — not width tier.
    const priceEl = document.querySelector('[data-product-price]');
    const hadRipCutApplied = priceEl?.hasAttribute('data-rip-cut-applied-cents');
    this.updateRipCutState(undefined, false);
    if (window.ripCutState?.active || hadRipCutApplied) {
      const delay = event?.type === 'length-changed' ? 500 : 120;
      this.scheduleRipCutPriceUpdate(delay);
    }

    const summaryDelay = event?.type === 'length-changed' ? 750 : 350;
    const summaryTrigger = event?.type === 'length-changed' ? 'length change' : 'variant change';
    this.schedulePriceSummaryLog(summaryTrigger, summaryDelay);

    // Length/variant updates finish after the length slider debounce (~300ms).
    // Re-match the width product variant once those option inputs have settled.
    if (event?.type === 'length-changed' || event?.type === 'theme:variant:change' || event?.type === 'variants-block:updated') {
      const delay = event?.type === 'length-changed' ? 550 : 180;
      this.scheduleWidthVariantSync(delay);
    }
  }

  scheduleWidthVariantSync(delay = 550) {
    clearTimeout(this._widthVariantSyncTimer);
    this._widthVariantSyncTimer = setTimeout(() => {
      const widthValue = parseInt(this.slider?.value, 10);
      if (!Number.isFinite(widthValue) || !this.jsonData?.length) return;

      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        this.handleVariantChange(nearestProduct);
        this.schedulePriceSummaryLog('width sync after length', 200);
      }
    }, delay);
  }

  scheduleRipCutPriceUpdate(delay = 120) {
    clearTimeout(this._ripCutPriceTimer);
    this._ripCutPriceTimer = setTimeout(() => {
      this.updateRipCutState(undefined, true);
    }, delay);
  }

  observeProductUpdates() {
    const productSection = this.closest('.product-section') || document.querySelector('.product-section');
    if (!productSection || typeof MutationObserver === 'undefined') return;

    this._productObserver = new MutationObserver(mutations => {
      if (this._updatingRipCutPrice) return;
      const priceChanged = mutations.some(mutation => {
        if (mutation.type === 'attributes') return mutation.target.matches?.('[data-product-price]');
        if (mutation.target.nodeType === 1 && mutation.target.matches?.('[data-product-price]')) return true;
        if (mutation.target.parentElement?.closest?.('[data-product-price]')) return true;
        return Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 && (node.matches?.('[data-product-price]') || node.querySelector?.('[data-product-price]'))
        );
      });
      if (priceChanged) {
        const priceEl = document.querySelector('[data-product-price]');
        const hadRipCutApplied = priceEl?.hasAttribute('data-rip-cut-applied-cents');
        const basePriceChanged = mutations.some(mutation =>
          mutation.type === 'attributes' && mutation.attributeName === 'data-base-price'
        );
        const bracket = document.querySelector('addon-bracket-selector');

        if (priceEl) priceEl.removeAttribute('data-rip-cut-applied-cents');
        this.updateRipCutState(undefined, false);

        if (bracket && typeof bracket.updatePrice === 'function') {
          // Bracket owns the final total; only recalculate when the variant base changed.
          if (basePriceChanged) {
            setTimeout(() => bracket.updatePrice(), 0);
          }
        } else if (window.ripCutState?.active || hadRipCutApplied) {
          this.scheduleRipCutPriceUpdate(120);
        }

        this.schedulePriceSummaryLog('variant price from theme', 250);
      }
    });
    this._productObserver.observe(productSection, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-base-price']
    });
  }

  getVariantPriceCents(variant) {
    if (!variant) return NaN;
    if (Number.isFinite(variant.price_cents)) return variant.price_cents;
    if (typeof variant.price === 'number') return variant.price;
    if (typeof variant.price === 'string') {
      const parsed = this.parseDisplayedMoney(variant.price);
      if (Number.isFinite(parsed)) return parsed;
    }
    return NaN;
  }

  syncVariantBasePrice(variant) {
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl || !variant) return;

    const variantCents = this.getVariantPriceCents(variant);
    if (!Number.isFinite(variantCents)) return;

    priceEl.setAttribute('data-base-price', String(variantCents));
    priceEl.removeAttribute('data-rip-cut-applied-cents');
    this.updateProductPrice();
  }

  updateProductPrice() {
    const bracket = document.querySelector('addon-bracket-selector');
    if (bracket && typeof bracket.updatePrice === 'function') {
      bracket.updatePrice();
      this.schedulePriceSummaryLog('rip cut applied', 80);
      return;
    }

    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;

    const rawBase = priceEl.getAttribute('data-base-price');
    if (rawBase === null) return;

    const basePriceCents = parseInt(rawBase, 10) || 0;
    const ripCutPriceCents = window.ripCutState?.active
      ? (window.ripCutState.totalPriceCents || 0)
      : 0;
    const totalCents = basePriceCents + ripCutPriceCents;
    const currencyFormatter = window.themeVendor?.themeCurrency;
    if (!currencyFormatter || !window.theme) return;

    const formatted = window.theme.settings?.currency_code_enable
      ? currencyFormatter.formatMoney(totalCents, theme.moneyFormat) + ` ${theme.currencyCode}`
      : currencyFormatter.formatMoney(totalCents, theme.moneyFormat);

    this._updatingRipCutPrice = true;
    if (ripCutPriceCents > 0) {
      priceEl.setAttribute('data-rip-cut-applied-cents', ripCutPriceCents);
    } else {
      priceEl.removeAttribute('data-rip-cut-applied-cents');
    }
    priceEl.innerHTML = formatted;
    setTimeout(() => { this._updatingRipCutPrice = false; }, 0);

    this.schedulePriceSummaryLog('rip cut applied', 80);
  }

  parseDisplayedMoney(value) {
    const match = String(value || '').replace(/\s/g, '').match(/-?\d[\d.,]*/);
    if (!match) return NaN;

    let amount = match[0];
    const lastDot = amount.lastIndexOf('.');
    const lastComma = amount.lastIndexOf(',');
    const separatorIndex = Math.max(lastDot, lastComma);

    if (separatorIndex >= 0 && amount.length - separatorIndex - 1 === 2) {
      const whole = amount.slice(0, separatorIndex).replace(/[.,]/g, '');
      const decimals = amount.slice(separatorIndex + 1);
      amount = `${whole}.${decimals}`;
    } else {
      amount = amount.replace(/[.,]/g, '');
    }

    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? Math.round(numericAmount * 100) : NaN;
  }

  debounceVariantChange(product, delay = 650) {
    if (this._variantDebounceTimer) {
      clearTimeout(this._variantDebounceTimer);
    }
    this._variantDebounceTimer = setTimeout(() => {
      this.handleVariantChange(product);
    }, delay);
  }

  updateSelectedValueDisplay(value) {
    if (!this.selectedValueDisplay) return;

    const numericValue = parseFloat(value);
    const inches = (numericValue / 25.4).toFixed(1);

    if (this.exactInput) {
      this.selectedValueDisplay.textContent = `(approx ${inches}in)`;
    } else {
      this.selectedValueDisplay.textContent = `${numericValue}mm (approx ${inches}in)`;
    }
  }

  updateKnob(value) {
    if (!this.knob) return;

    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);
    const percent = ((value - min) / (max - min)) * 100;

    this.knob.style.left = `${percent}%`;
    this.knob.textContent = `${value}mm`;
  }

  findNearestWidthProduct(targetWidth) {
    if (!this.jsonData || !Array.isArray(this.jsonData)) return null;

    const target = Math.round(targetWidth);
    // Find all products with width >= target, then pick the smallest one
    let nextProduct = null;
    let minLargerWidth = Infinity;
    for (const product of this.jsonData) {
      const prodWidth = parseInt(product.product_metafields?.width || 0);
      if (prodWidth === 0) continue;
      if (prodWidth >= target && prodWidth < minLargerWidth) {
        minLargerWidth = prodWidth;
        nextProduct = product;
      }
    }
    // If no larger width found, fallback to the largest available
    if (!nextProduct) {
      let maxWidth = -Infinity;
      for (const product of this.jsonData) {
        const prodWidth = parseInt(product.product_metafields?.width || 0);
        if (prodWidth > maxWidth) {
          maxWidth = prodWidth;
          nextProduct = product;
        }
      }
    }
    return nextProduct;
  }

  handleVariantChange(product, retryCount = 0) {
    const variantBlock = document.querySelector(this.selectors.variantsBlock);
    if (!product?.variants) return;

    const optionElements = variantBlock ? Array.from(variantBlock.querySelectorAll('[data-option-position]')) : [];

    // Sort by data-option-position (ascending)
    optionElements.sort((a, b) => {
      return parseInt(a.getAttribute('data-option-position'), 10) - parseInt(b.getAttribute('data-option-position'), 10);
    });

    const selectedValues = optionElements.map(optionEl => {
      const hiddenInput = optionEl.querySelector('input[type="hidden"]');
      if (hiddenInput && hiddenInput.value) return hiddenInput.value;

      const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
      if (checkedRadio) return checkedRadio.value;

      const checkedSwatch = optionEl.querySelector('[swatch-option-selector]:checked, [data-addon-input]:checked');
      if (checkedSwatch) return checkedSwatch.value;

      const activeLink = optionEl.querySelector('a.active[data-value], a[data-value].active');
      if (activeLink) return activeLink.getAttribute('data-value');

      return null;
    });

    if (selectedValues.includes(null)) {
      if (retryCount < 6) {
        setTimeout(() => this.handleVariantChange(product, retryCount + 1), 150);
        return;
      }
      console.warn('[Width Prime] Options not ready for variant match:', selectedValues);
      return;
    }

    const matchingVariant = product.variants.find(variant =>
      variant.options.every((option, index) => option === selectedValues[index])
    );

    if (matchingVariant) {
      window.currentMatchingVariant = matchingVariant;
      this.applyVariantSelection(matchingVariant, product);
      this.schedulePriceSummaryLog('width tier variant matched', 400);
      return;
    }

    window.currentMatchingVariant = null;
    console.warn('[Width Prime] No exact variant match for width product.', {
      selectedValues,
      width: product.product_metafields?.width
    });

    // Try fallback: find variant with most matching options (at least 1 match)
    let bestMatch = null;
    let bestMatchCount = 0;
    product.variants.forEach(variant => {
      let matchCount = 0;
      for (let i = 0; i < selectedValues.length; i++) {
        if (variant.options[i] === selectedValues[i]) matchCount++;
      }
      if (matchCount > bestMatchCount) {
        bestMatch = variant;
        bestMatchCount = matchCount;
      }
    });

    if (bestMatch && bestMatchCount > 0 && bestMatchCount < selectedValues.length) {
      window.currentMatchingVariant = bestMatch;
      this.applyVariantSelection(bestMatch, product, 'width-prime-change-fallback');
    }
  }

  applyVariantSelection(matchingVariant, product, updateSource = 'width-prime-change') {
    const idInput = document.querySelector("input[type='hidden'][name='id']");
    const variantChanged = idInput && String(idInput.value) !== String(matchingVariant.id);

    if (idInput) {
      idInput.value = matchingVariant.id;
      if (variantChanged && typeof idInput.dispatchEvent === 'function') {
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else {
      console.warn("[Width Prime] Could not find input[type='hidden'][name='id']");
    }

    // Width tier changes select a different variant price. Sync data-base-price
    // from the matched JSON variant so addon/rip-cut layers recalculate live.
    this.updateRipCutState(parseInt(this.slider?.value, 10), false);
    this.syncVariantBasePrice(matchingVariant);

    if (!matchingVariant.url || typeof matchingVariant.url !== 'string' || !window.location) return;

    window.history.replaceState({}, '', matchingVariant.url);
    this.fetchAndApplyProductHtml(matchingVariant.url, updateSource);
  }

  fetchAndApplyProductHtml(url, updateSource = 'width-prime-change') {
    const useHtml = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const newVariantsBlock = tempDiv.querySelector('.product__block__variants');
      const currentVariantsBlock = document.querySelector('.product__block__variants');
      if (newVariantsBlock && currentVariantsBlock) {
        if (newVariantsBlock.innerHTML !== currentVariantsBlock.innerHTML) {
          currentVariantsBlock.replaceWith(newVariantsBlock);

          document.dispatchEvent(new CustomEvent('variants-block:updated', {
            detail: { source: updateSource },
            bubbles: true
          }));

          const productSection = document.querySelector('[data-section-id][data-section-type="product"]') ||
            document.querySelector('.product-page[data-section-id]');
          if (productSection) {
            const sectionId = productSection.getAttribute('data-section-id');
            document.dispatchEvent(new CustomEvent('shopify:section:load', {
              detail: { sectionId },
              bubbles: true
            }));
          }
        }
      } else {
        console.warn('[Width Prime] Could not find new or current variants block.');
      }

      const newDescriptionBlock = tempDiv.querySelector('.product__block__description');
      const currentDescriptionBlock = document.querySelector('.product__block__description');
      if (newDescriptionBlock && currentDescriptionBlock) {
        const newAccordionInners = newDescriptionBlock.querySelectorAll('.accordion-content__inner');
        const currentAccordionInners = currentDescriptionBlock.querySelectorAll('.accordion-content__inner');
        if (newAccordionInners.length === currentAccordionInners.length) {
          newAccordionInners.forEach((newInner, idx) => {
            const currentInner = currentAccordionInners[idx];
            if (currentInner && newInner) {
              currentInner.innerHTML = newInner.innerHTML;
            }
          });
        } else {
          console.warn('[Width Prime] Accordion inner count mismatch, skipping update.');
        }
      }
    };

    if (OptionWidthPrimeRange.productHtmlCache[url]) {
      useHtml(OptionWidthPrimeRange.productHtmlCache[url]);
      return;
    }

    fetch(url)
      .then(res => res.text())
      .then(html => {
        OptionWidthPrimeRange.productHtmlCache[url] = html;
        useHtml(html);
      })
      .catch(err => {
        console.error('[Width Prime] Failed to fetch or replace product blocks:', err);
      });
  }


}

customElements.define('option-width-prime-range', OptionWidthPrimeRange);
